/**
 * Scope 3 page — Business Travel (Cat 6) + Employee Commute (Cat 7) + My Activities + Surveys
 *
 * Simple three-tab UI. Factor catalog drives the dropdowns so selections
 * always match a real DEFRA factor — no typed fields for mode/vehicle/fuel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plane, Car, Train, Ship, Building2, Home, Trash2, Copy, Plus, Truck, Bot } from 'lucide-react';
import { fmtNumber, usePalette } from '../components/charts/chartKit';
import { modeName, shortPlace } from '../components/shipments/legLabels';
import AppLayout from '../components/layout/AppLayout';
import PageHeader, { SCOPE3_HEADS } from '../components/layout/PageHeader';
import useShipments from '../hooks/useShipments';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND}/api`;
import {
  getFactorCatalog,
  calculatePreview,
  saveActivity,
  listActivities,
  activitySummary,
  deleteActivity,
} from '../services/scope3Service';
import {
  createSurvey,
  listSurveys,
  closeSurvey,
  getSurveySubmissions,
} from '../services/commuteSurveyService';

const MODE_ICONS = {
  car: Car, motorbike: Car, taxi: Car, bus: Car,
  rail: Train, flight: Plane, ferry: Ship,
  hotel: Building2, homeworking: Home,
  // shipment leg modes
  road: Truck, freight: Truck, truck: Truck,
  air: Plane, sea: Ship, water: Ship,
};

const ModeIcon = ({ mode }) => {
  const I = MODE_ICONS[mode] || Car;
  return <I className="h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />;
};

const categoryLabel = (cat) => (cat === 6 ? 'Business Travel' : cat === 7 ? 'Employee Commute' : `Cat ${cat}`);

const TAB_TITLES = {
  travel: 'Business Travel',
  commute: 'Employee Commute',
  activities: 'My Activities',
  surveys: 'Commute Surveys',
};

// Map a Cat 4/9 shipment row into the same shape as a scope3 activity so they
// can render in one unified table. Each shipment becomes 1 row keyed by the
// `good.ghg_category` (upstream → Cat 4, downstream → Cat 9).
function shipmentToActivity(s) {
  const goodsCat = s.good?.ghg_category || 'upstream';
  const ghgCategory = goodsCat === 'downstream' ? 9 : goodsCat === 'company_owned' ? null : 4;
  const firstLeg = s.transport_legs?.[0];
  const lastLeg = s.transport_legs?.[s.transport_legs.length - 1];
  return {
    id: s.id,
    ghg_category: ghgCategory,
    activity_date: s.created_at?.slice(0, 10),
    created_at: s.created_at,
    mode: firstLeg?.transport_mode || firstLeg?.mode || 'transport',
    subtype: s.good?.name,
    fuel_type: firstLeg?.fuel_type,
    cabin_class: null,
    origin: shortPlace(firstLeg?.from_location?.address),
    destination: shortPlace(lastLeg?.to_location?.address),
    legCount: (s.transport_legs || []).length,
    activity_value: Number(s.total_distance || 0),
    activity_unit: 'km',
    co2e_kg: Number(s.total_emissions || 0),
    _source: 'shipment',
  };
}

export default function ScopeThree() {
  const params = useParams();
  const tab = TAB_TITLES[params.tab] ? params.tab : 'travel';
  const [catalogCat6, setCatalogCat6] = useState(null);
  const [catalogCat7, setCatalogCat7] = useState(null);
  const [activities, setActivities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const { shipments, reload: reloadShipments } = useShipments();

  useEffect(() => {
    getFactorCatalog({ ghg_category: 6 }).then(setCatalogCat6).catch(() => toast.error('Failed to load travel factors'));
    getFactorCatalog({ ghg_category: 7 }).then(setCatalogCat7).catch(() => toast.error('Failed to load commute factors'));
  }, []);

  useEffect(() => {
    listActivities({ limit: 500 }).then(setActivities).catch(() => {});
    activitySummary().then(setSummary).catch(() => {});
  }, [refreshTick]);

  // Unified list — Cat 6/7 from ghg_activities + Cat 4/9 from shipments.
  // Sorted by date desc so newest is at the top regardless of source.
  const allActivities = useMemo(() => {
    const fromShipments = (shipments || [])
      .map(shipmentToActivity)
      .filter((a) => a.ghg_category != null);
    const combined = [...activities, ...fromShipments];
    combined.sort((a, b) => {
      const da = a.activity_date || a.created_at || '';
      const db = b.activity_date || b.created_at || '';
      return db.localeCompare(da);
    });
    return combined;
  }, [activities, shipments]);

  const onSaved = () => {
    setRefreshTick((t) => t + 1);
    reloadShipments();
  };

  return (
    <AppLayout title={`Scope 3 · ${TAB_TITLES[tab]}`}>
      <div className="max-w-6xl mx-auto">
        <PageHeader {...SCOPE3_HEADS[tab]} />
        {summary && (
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Same Liquid Glass tiles as the dashboard KPI row (light follows the pointer). */}
            <SummaryTile tint label="Total CO₂e recorded" value={fmtNumber(summary.total_co2e_kg || 0)} unit="kg CO₂e" />
            <SummaryTile label="Entries" value={(summary.entry_count || 0).toLocaleString()} />
            <div className="liquid-glass p-5">
              <p className="text-sm font-medium text-foreground/75">By category</p>
              <ul className="mt-2 space-y-1.5">
                {Object.entries(summary.by_category || {}).length === 0 && <li className="text-lg font-semibold">—</li>}
                {Object.entries(summary.by_category || {}).map(([c, v]) => (
                  <li key={c} className="flex items-baseline justify-between gap-3 text-sm">
                    <span className="text-foreground">{categoryLabel(+c)}</span>
                    <span className="tabular-nums font-semibold text-foreground">
                      {fmtNumber(v)} <span className="font-normal text-muted-foreground">kg</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === 'travel' && (
          <>
            <div className="mb-3 flex justify-end">
              <Link to="/shanthi">
                <Button variant="outline" size="sm" className="gap-2 rounded-full">
                  <Bot className="h-4 w-4" aria-hidden="true" /> Ask Trace
                </Button>
              </Link>
            </div>
            <ActivityForm
              ghgCategory={6}
              activityType="business_travel"
              catalog={catalogCat6}
              onSaved={onSaved}
            />
          </>
        )}

        {tab === 'commute' && (
          <ActivityForm
            ghgCategory={7}
            activityType="employee_commute"
            catalog={catalogCat7 || catalogCat6}
            onSaved={onSaved}
          />
        )}

        {tab === 'activities' && <ActivityList activities={allActivities} onDelete={onSaved} />}

        {tab === 'surveys' && <SurveysPanel />}
      </div>
    </AppLayout>
  );
}

function SummaryTile({ label, value, unit, tint = false }) {
  return (
    <div className={tint ? 'liquid-glass liquid-glass--tint p-5' : 'liquid-glass p-5'}>
      <p className={`text-sm font-medium ${tint ? 'text-primary' : 'text-foreground/75'}`}>{label}</p>
      <p className="mt-2 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tracking-[-0.02em] tabular-nums text-foreground">{value}</span>
        {unit && <span className="text-sm text-foreground/70">{unit}</span>}
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------
// ActivityForm — shared by Cat 6 and Cat 7
// --------------------------------------------------------------------------

function ActivityForm({ ghgCategory, activityType, catalog, onSaved }) {
  const modes = catalog?.modes || {};
  const [mode, setMode] = useState('');
  const [group, setGroup] = useState('');
  const [factorIdx, setFactorIdx] = useState(''); // selected index in the group array
  const [value, setValue] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [purpose, setPurpose] = useState('');
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const groups = mode ? Object.keys(modes[mode] || {}) : [];
  const factorOptions = mode && group ? (modes[mode][group] || []) : [];
  const selectedFactor = factorIdx !== '' ? factorOptions[Number(factorIdx)] : null;

  useEffect(() => { setGroup(''); setFactorIdx(''); setPreview(null); }, [mode]);
  useEffect(() => { setFactorIdx(''); setPreview(null); }, [group]);
  useEffect(() => { setPreview(null); }, [factorIdx, value, passengers]);

  const canCalc = mode && selectedFactor && Number(value) > 0;
  // Per-passenger factors (flight, rail, bus, taxi, ferry): the user enters the trip distance in km
  // and the number of travellers; the backend converts km × travellers → passenger.km. Sending the
  // factor's own unit here made the backend ignore the travellers count (3 people counted as 1).
  const isPerPassenger = selectedFactor?.activity_unit === 'passenger.km';

  const buildActivity = () => ({
    ghg_category: ghgCategory,
    activity_type: activityType,
    mode,
    activity_value: Number(value),
    activity_unit: isPerPassenger ? 'km' : selectedFactor.activity_unit,
    passengers: Math.min(Math.max(Math.floor(Number(passengers)) || 1, 1), 999),
    vehicle_type: selectedFactor.vehicle_type,
    size_class: selectedFactor.size_class,
    fuel_type: selectedFactor.fuel_type,
    cabin_class: selectedFactor.cabin_class,
    radiative_forcing: selectedFactor.radiative_forcing,
    region: selectedFactor.region,
    origin: origin || undefined,
    destination: destination || undefined,
    activity_date: date || undefined,
    purpose: purpose || undefined,
  });

  const handlePreview = async () => {
    if (!canCalc) return;
    try {
      const res = await calculatePreview(buildActivity());
      setPreview(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Calculation failed');
    }
  };

  const handleSave = async () => {
    if (!canCalc) return;
    setSaving(true);
    try {
      const res = await saveActivity(buildActivity());
      toast.success(`Saved: ${Number(res.co2e_kg || 0).toFixed(2)} kg CO₂e`);
      setPreview(res);
      setValue('');
      setOrigin('');
      setDestination('');
      setPurpose('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!catalog) return <p className="text-slate-500">Loading UK DESNZ 2026 factors…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {ghgCategory === 6 ? <Plane className="w-5 h-5" /> : <Car className="w-5 h-5" />}
          {ghgCategory === 6 ? 'Business Travel entry' : 'Employee Commute entry'}
        </CardTitle>
        <CardDescription>
          UK DESNZ 2026 factors — GHG Protocol Scope 3 Category {ghgCategory}. Select transport
          mode → activity group → specific variant.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Transport mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger><SelectValue placeholder="Pick a mode" /></SelectTrigger>
              <SelectContent>
                {Object.keys(modes).sort().map((m) => (
                  <SelectItem key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1).replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Activity group</Label>
            <Select value={group} onValueChange={setGroup} disabled={!mode}>
              <SelectTrigger><SelectValue placeholder={mode ? 'Pick a group' : 'Select mode first'} /></SelectTrigger>
              <SelectContent>
                {groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Specific factor</Label>
            <Select value={factorIdx} onValueChange={setFactorIdx} disabled={!group}>
              <SelectTrigger><SelectValue placeholder={group ? 'Pick a factor' : 'Select group first'} /></SelectTrigger>
              <SelectContent>
                {factorOptions.map((f, i) => (
                  <SelectItem key={i} value={String(i)}>
                    {[
                      f.vehicle_type,
                      f.cabin_class,
                      f.fuel_type,
                      f.radiative_forcing === true ? 'with RF' : f.radiative_forcing === false ? 'no RF' : null,
                      f.region && f.region !== 'UK' ? f.region : null,
                    ].filter(Boolean).join(' · ')} — {f.co2e_total} kg/{f.activity_unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selectedFactor && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{isPerPassenger ? 'Distance (km)' : `Amount (${selectedFactor.activity_unit})`}</Label>
              <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" />
            </div>
            {isPerPassenger && (
              <div>
                <Label>Travelers</Label>
                <Input type="number" min="1" max="999" step="1" value={passengers} onChange={(e) => setPassengers(e.target.value)} />
                <p className="mt-1 text-xs text-muted-foreground">Emissions = distance × travelers × per-passenger factor.</p>
              </div>
            )}
            <div>
              <Label>Activity date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Origin (optional)</Label>
            <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="e.g. London" />
          </div>
          <div>
            <Label>Destination (optional)</Label>
            <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Edinburgh" />
          </div>
          <div>
            <Label>Purpose (optional)</Label>
            <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Client visit" />
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={!canCalc}>Preview</Button>
          <Button onClick={handleSave} disabled={!canCalc || saving}>{saving ? 'Saving…' : 'Save entry'}</Button>
        </div>

        {preview && (
          <div className="rounded-lg border p-4 bg-emerald-50">
            <div className="text-2xl font-semibold text-emerald-900">{Number(preview.co2e_kg || 0).toFixed(3)} kg CO₂e</div>
            <div className="text-sm text-emerald-800 mt-1">
              Factor: {preview.factor_used.display_name} — {preview.factor_used.co2e_total} kg CO₂e/{preview.factor_used.activity_unit}
            </div>
            {preview.warnings?.length > 0 && (
              <ul className="text-xs text-amber-700 mt-2 list-disc pl-5">
                {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --------------------------------------------------------------------------
// ActivityList
// --------------------------------------------------------------------------

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 4, label: 'Cat 4 — Upstream', cat: 4 },
  { id: 9, label: 'Cat 9 — Downstream', cat: 9 },
  { id: 6, label: 'Cat 6 — Business Travel', cat: 6 },
  { id: 7, label: 'Cat 7 — Commute', cat: 7 },
];

function ActivityList({ activities, onDelete }) {
  const [filter, setFilter] = useState('all');
  const palette = usePalette();

  const filtered = useMemo(() => {
    if (filter === 'all') return activities;
    return activities.filter((a) => a.ghg_category === filter);
  }, [activities, filter]);

  const handleDelete = async (a) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      if (a._source === 'shipment') {
        await axios.delete(`${API}/shipments/bulk`, { data: { shipment_ids: [a.id] } });
      } else {
        await deleteActivity(a.id);
      }
      toast.success('Deleted');
      onDelete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-3">
      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="mr-1 text-sm text-muted-foreground">Filter:</span>
        {FILTERS.map((f) => {
          const count = f.id === 'all'
            ? activities.length
            : activities.filter((a) => a.ghg_category === f.cat).length;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'border border-border bg-card text-foreground hover:bg-secondary'
              }`}
            >
              {f.cat && <span className="h-2 w-2 rounded-[2px]" style={{ background: palette.category(f.cat) }} aria-hidden="true" />}
              {f.label}
              <span className={`tabular-nums ${active ? 'opacity-80' : 'text-muted-foreground'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <p className="rounded-xl border bg-card p-4 text-sm text-muted-foreground">
          {activities.length === 0
            ? 'No activities yet — add a shipment or business travel entry.'
            : 'No activities match the selected filter.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card shadow">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="whitespace-nowrap px-4 py-3 font-semibold">Date</th>
                <th className="whitespace-nowrap px-3 py-3 font-semibold">Category</th>
                <th className="whitespace-nowrap px-3 py-3 font-semibold">Mode</th>
                <th className="px-3 py-3 font-semibold">Description</th>
                <th className="whitespace-nowrap px-3 py-3 text-right font-semibold">Activity</th>
                <th className="whitespace-nowrap px-3 py-3 text-right font-semibold">kg CO₂e</th>
                <th className="w-12 px-2 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const iso = a.activity_date || a.created_at?.slice(0, 10);
                const d = iso ? new Date(`${iso.slice(0, 10)}T00:00:00`) : null;
                const date = d && !Number.isNaN(d.getTime())
                  ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                  : '—';
                const details = [a.fuel_type && a.fuel_type !== 'unknown' ? a.fuel_type : null, a.cabin_class].filter(Boolean).join(' · ');
                return (
                  <tr key={`${a._source || 'scope3'}-${a.id}`} className="border-t border-border/70 align-top">
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-muted-foreground">{date}</td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
                        <span className="h-2 w-2 rounded-[2px]" style={{ background: palette.category(a.ghg_category) }} aria-hidden="true" />
                        Cat {a.ghg_category}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <ModeIcon mode={a.mode} />
                        {modeName(a.mode)}
                      </span>
                    </td>
                    <td className="min-w-[220px] px-3 py-3">
                      <span className="font-medium text-foreground">{a.subtype || modeName(a.mode)}</span>
                      {details && <span className="text-muted-foreground"> · {details}</span>}
                      {a.origin && a.destination ? (
                        <span className="mt-0.5 block text-xs text-muted-foreground">{a.origin} → {a.destination}</span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right tabular-nums text-muted-foreground">
                      {fmtNumber(a.activity_value)} {a.activity_unit}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-foreground">{fmtNumber(a.co2e_kg, 2)}</td>
                    <td className="px-2 py-2 text-right">
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(a)} aria-label="Delete activity" title="Delete activity">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// SurveysPanel — admin-side: create survey, copy share link, view submissions
// --------------------------------------------------------------------------

function SurveysPanel() {
  const [surveys, setSurveys] = useState([]);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [days, setDays] = useState(220);
  const [weeks, setWeeks] = useState(52);
  const [detail, setDetail] = useState(null);

  const reload = () => listSurveys().then(setSurveys).catch(() => {});
  useEffect(() => { reload(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    try {
      const s = await createSurvey({
        name, description: desc,
        working_days_per_year: Number(days) || 220,
        weeks_covered: Number(weeks) || 52,
      });
      setName(''); setDesc('');
      await navigator.clipboard.writeText(s.share_url).catch(() => {});
      toast.success('Survey created — share link copied to clipboard');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  const handleClose = async (s) => {
    try { await closeSurvey(s.id); toast.success('Survey closed'); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
  };

  const handleView = async (s) => {
    try { const d = await getSurveySubmissions(s.id); setDetail(d); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  };

  const handleCopy = async (url) => {
    await navigator.clipboard.writeText(url).catch(() => {});
    toast.success('Share link copied');
  };

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Create commute survey</CardTitle>
          <CardDescription>Generate a shareable public link. Employees submit their typical weekly commute — no login required.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="FY25 Commute Survey" /></div>
          <div><Label>Description</Label><Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Tell employees why you're asking" /></div>
          <div><Label>Working days per year</Label><Input type="number" value={days} onChange={(e) => setDays(e.target.value)} /></div>
          <div><Label>Weeks covered</Label><Input type="number" value={weeks} onChange={(e) => setWeeks(e.target.value)} /></div>
          <div className="md:col-span-2"><Button onClick={handleCreate}><Plus className="w-4 h-4 mr-1" />Create survey</Button></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Your surveys</CardTitle></CardHeader>
        <CardContent>
          {surveys.length === 0 ? (
            <p className="text-slate-500">No surveys yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr><th className="p-2">Name</th><th className="p-2">Status</th><th className="p-2">Link</th><th /></tr>
              </thead>
              <tbody>
                {surveys.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="p-2">{s.name}</td>
                    <td className="p-2"><Badge>{s.status}</Badge></td>
                    <td className="p-2 font-mono text-xs truncate max-w-sm">{s.share_url}</td>
                    <td className="p-2 flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => handleCopy(s.share_url)}><Copy className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleView(s)}>View</Button>
                      {s.status === 'open' && <Button size="sm" variant="outline" onClick={() => handleClose(s)}>Close</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card>
          <CardHeader>
            <CardTitle>{detail.survey.name} — Submissions</CardTitle>
            <CardDescription>
              {detail.respondents} respondents · {detail.total_co2e_kg} kg CO₂e total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="p-2">Name</th>
                  <th className="p-2">Dept</th>
                  <th className="p-2">WFH days/wk</th>
                  <th className="p-2">Entries</th>
                  <th className="p-2 text-right">kg CO₂e/yr</th>
                </tr>
              </thead>
              <tbody>
                {detail.submissions.map((s) => (
                  <tr key={s.id} className="border-t">
                    <td className="p-2">{s.respondent_name || '—'}</td>
                    <td className="p-2">{s.department || '—'}</td>
                    <td className="p-2">{s.wfh_days_per_week}</td>
                    <td className="p-2">
                      {(s.entries || []).map((e, i) => (
                        <div key={i} className="text-xs text-slate-600">
                          {e.mode} · {e.one_way_distance}{e.activity_unit} × {e.days_per_week} d/wk
                        </div>
                      ))}
                    </td>
                    <td className="p-2 text-right font-medium">{Number(s.total_co2e_kg || 0).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
